// R5-8 — THE MEMBER-FACING LABELS, LIFTED OUT OF App.tsx VERBATIM.
//
// Every function here is pure, and every one of them writes words a member reads: what a failed
// sign-in tells her to DO next, what a saved reading is called on her shelf, how long a generation
// has been running, and what order her own things are listed in. Pure and member-facing is the worst
// combination to leave untested, which is what living inside a TSX file amounted to.
//
// Bodies unchanged. Asserted in `scripts/r5-extraction.mjs`.

// X8-HOTFIX: turn the server's reason code into something a member can act on. Every one of
// these was previously a blank modal. The state reasons are the mobile-only class: the sign-in
// took too long, was started twice, or ran inside an app's built-in browser that dropped the
// cookie, so the guidance names the actual next move rather than "try again".
export function authErrorMessage(reason: string | null): string {
  switch (reason) {
    case "disallowed_useragent":
      return "Google will not accept a sign-in inside another app's browser. Open 12thhouse.ai in Safari or Chrome, then tap Continue with Google.";
    case "access_denied":
      return "That sign-in was cancelled on the Google screen. Tap Continue with Google to try again.";
    case "state-absent":
    case "state-expired":
    case "state-malformed":
    case "state-bad-signature":
    case "state":
      return "That sign-in did not make it back to the house. This usually means it sat too long on the Google screen, or it was started twice. Tap Continue with Google and finish it in one go. If you opened this from a link inside another app, open 12thhouse.ai in Safari or Chrome first.";
    case "token":
    case "code":
      return "Google could not finish confirming that account. Tap Continue with Google to try once more.";
    case "profile":
      return "Google signed you in but did not release an email address for that account. Try a different Google account, or use the email form below.";
    default:
      return "Google sign-in did not complete. Tap Continue with Google to try again.";
  }
}

// R32 — THE TABLE IS DATA, AND IT IS EXPORTED, BECAUSE THE DEFAULT COLLIDES WITH A REAL ANSWER.
//
// This was an if-chain ending in `return "Birth Chart"`, and "Birth Chart" is also the CORRECT label
// for one real type — so no check on the returned value can tell a rostered answer from a
// fallthrough (R28's M5: when a lookup's default collides with a legitimate answer, check the TABLE,
// not the result). Measured over the server's own roster, three real types were falling through and
// reading as "Birth Chart" on her shelf: `note` (a member's OWN written note), `palm-reading`, and
// `bubble-reading`. Nothing threw and nothing logged; the chip just said the wrong thing.
//
// W12-2 — **THE MAGIC ROOM'S TRUE NOUNS.** Every magic type once fell through to "Birth Chart", so a
// spell she kept in My Room sat on her shelf wearing the name of a reading she never ran. These
// strings are held to `magicHeading` in src/magicRoom.js by the W12-2 and R32 fixtures — this file's
// entry requirement is that it imports NOTHING, so the two are checked against each other rather
// than one importing the other. R32 aligned `chant` / `affirmation` / `incantation` to that heading's
// own wording; W12-2's check ran over eight hand-typed types and could not see the drift on those.
// P2-4B: the table and its resolver MOVED to src/reportLabels.js, a SHARED law, because a canned
// generation push has to name the reading on the SERVER and this file is client-only TypeScript.
// It is NOT re-exported from here: this file's entry requirement is that it imports NOTHING (the
// loader every src/lib fixture uses strips types and imports a data URL, which cannot resolve a
// relative specifier), so an "import it and pass it through" convenience would take the whole module
// out of reach of its own gate. The readers moved to the data instead.

// G3 — THE IN-ROOM GENERATION QUEUE STRIP. One shared component under every generate button. The
// room must never look like it lost the member's work: when they return mid-run it SHOWS the work
// still going (pulsing, with elapsed time), tells them warmly they can roam the house and it will
// land on this room's shelf, and flips to a READY entry that opens the result when it finishes.
// Hidden entirely when this room has nothing in flight and nothing freshly ready. Copy here is
// authored to the voice law (no machinery, no self-narration, no em dashes).
export function elapsedLabel(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r.toString().padStart(2, "0")}s` : `${r}s`;
}

// OV2-6 — NEWEST FIRST, SITE-WIDE. This REVERSES the earlier I3 ruling (oldest first, newest at the
// end) on the owner's word: every shelf now lists newest at top, oldest at bottom, in every shelf view
// and every drawer. It stays ONE shared sort so no panel rolls its own and no surface can drift.
export function newestFirst<T extends { createdAt?: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const ta = a.createdAt ? Date.parse(a.createdAt) : 0;
    const tb = b.createdAt ? Date.parse(b.createdAt) : 0;
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });
}
